import { Cta, SanityCtaProps } from "@/components/cta"
import { SocialIconLink } from "@/components/social-icon-link"
import { invokeFunnelDev } from "@/features/funnel/api/dev"
import { useLocaleContext } from "@/modules/i18n/context"
import { RichText, RichTextValue } from "@/components/rich-text"
import { Checkbox } from "@/ui/checkbox"
import { Dropzone } from "@/ui/dropzone"
import SanityImageField from "@/integrations/sanity/components/SanityImageField"
import { Input } from "@/ui/input"
import { Switch } from "@/ui/switch"
import { Textarea } from "@/ui/textarea"
import { cn } from "@/utils/cn"
import { isAllowedCvExtension } from "@/features/funnel/cv-upload-constraints"
import useEmblaCarousel from "embla-carousel-react"
import {
  ArrowLeft,
  ArrowLeftCircle,
  ArrowRight,
  LoaderCircle,
} from "lucide-react"
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react"
import { SanityImageAsset, SanityReference } from "sanity-codegen"
import { toast } from "sonner"
import z from "zod"
import { FunnelDevPreferenceType } from "@/backend/commands/funnel/types"

interface FunnelCompanyProps {
  content: {
    alertTitle: string
    alertDescription: string
    contactTitle: string
    cvDescription: string
    cvSubtitle: string
    emailLabel: string
    firstNameLabel: string
    freelanceLabel: string
    jobLength: FunnelDevPreferenceType[]
    jobLengthLabel: string
    jobType: FunnelDevPreferenceType[]
    jobTypeLabel: string
    lastNameLabel: string
    linkedinDescription: string
    linkedinErrorMessage: string
    linkedinLabel: string
    linkedinSubtitle: string
    phoneNumberLabel: string
    devPowerLabel: string
    devPowerPlaceholder: string
    preferencesTitle: string
    profileTitle: string
    privacyLink: SanityCtaProps
    successImage?: {
      asset: SanityReference<SanityImageAsset>
      alt?: string
    }
    socials: {
      title?: string
      url: string
      image?: {
        asset: SanityReference<SanityImageAsset>
      }
    }[]
    instagramReelUrl?: string
    successMessage: string
    successTitle: string
    sisterFunnelIntro?: RichTextValue
    sisterFunnelCta?: SanityCtaProps
  }
}

const phoneNumberRegex = /^(?=(?:\D*\d){7,15}\D*$)\+?[0-9().\-\s]+$/

const createProfileStepSchema = (
  messages: {
    urlInvalid: string
    freelancerRequired: string
  }
) =>
  z.object({
    linkedinUrl: z.string().trim().url(messages.urlInvalid),
    isFreelancer: z.literal(true, {
      message: messages.freelancerRequired,
    }),
  })

const createPreferencesSchema = (messages: {
  devPowerRequired: string
  jobTypeRequired: string
  jobLengthRequired: string
}) =>
  z.object({
    devPower: z.string().trim().min(1, messages.devPowerRequired),
    jobTypes: z
      .array(
        z.object({
          label: z.string().trim().min(1),
          value: z.string().trim().min(1),
        })
      )
      .min(1, messages.jobTypeRequired),
    jobLengths: z
      .array(
        z.object({
          label: z.string().trim().min(1),
          value: z.string().trim().min(1),
        })
      )
      .min(1, messages.jobLengthRequired),
  })

const createContactSchema = (messages: {
  firstNameRequired: string
  lastNameRequired: string
  emailInvalid: string
  phoneInvalid: string
}) =>
  z.object({
    firstName: z.string().trim().min(1, messages.firstNameRequired),
    lastName: z.string().trim().min(1, messages.lastNameRequired),
    email: z.string().trim().email(messages.emailInvalid),
    phoneNumber: z
      .string()
      .trim()
      .regex(phoneNumberRegex, messages.phoneInvalid),
  })

export function FunnelDev({ content }: FunnelCompanyProps) {
  const { locale, $ } = useLocaleContext()
  const [funnelData, setFunnelData] = useState({
    linkedinUrl: "",
    cvFile: null as File | null,
    isFreelancer: false,
    devPower: "",
    jobTypes: [] as FunnelDevPreferenceType[],
    jobLengths: [] as FunnelDevPreferenceType[],
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  })

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: "x",
    watchDrag: false,
  })
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => setActiveStep(emblaApi.selectedScrollSnap())
    emblaApi.on("select", onSelect)
    onSelect()
    return () => {
      emblaApi.off("select", onSelect)
    }
  }, [emblaApi])

  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set("step", String(activeStep + 1))
    window.history.replaceState({}, "", url)
  }, [activeStep])

  return (
    <div className="overflow-hidden py-8" ref={emblaRef}>
      <div className="flex w-full">
        <FunnelSlide active={activeStep === 0}>
          <StepProfile
            $={{
              alertTitle: content.alertTitle,
              alertDescription: content.alertDescription,
              title: content.profileTitle,
              linkedinSubtitle: content.linkedinSubtitle,
              linkedinDescription: content.linkedinDescription,
              linkedinLabel: content.linkedinLabel,
              cvSubtitle: content.cvSubtitle,
              cvDescription: content.cvDescription,
              freelanceLabel: content.freelanceLabel,
            }}
            sisterFunnelIntro={content.sisterFunnelIntro}
            sisterFunnelCta={content.sisterFunnelCta}
            onContinue={(data) => {
              setFunnelData((p) => ({ ...p, ...data }))
              emblaApi?.scrollNext()
            }}
          />
        </FunnelSlide>
        <FunnelSlide active={activeStep === 1}>
          <Preferences
            $={{
              title: content.preferencesTitle,
              devPowerLabel: content.devPowerLabel,
              devPowerPlaceholder: content.devPowerPlaceholder,
              jobTypeLabel: content.jobTypeLabel,
              jobLengthLabel: content.jobLengthLabel,
            }}
            jobTypes={content.jobType}
            jobLengths={content.jobLength}
            onBack={() => emblaApi?.scrollPrev()}
            onContinue={(data) => {
              setFunnelData((p) => ({ ...p, ...data }))
              emblaApi?.scrollNext()
            }}
          />
        </FunnelSlide>
        <FunnelSlide active={activeStep === 2}>
          <Contact
            $={{
              title: content.contactTitle,
              firstNameLabel: content.firstNameLabel,
              lastNameLabel: content.lastNameLabel,
              emailLabel: content.emailLabel,
              phoneNumberLabel: content.phoneNumberLabel,
            }}
            privacyLink={
              <div className="text-inherit">
                {$.funnelCompany.privacy}
                <Cta
                  className="text-primary-500"
                  variant="blank"
                  sanity={content.privacyLink}
                />
              </div>
            }
            onBack={() => emblaApi?.scrollPrev()}
            onContinue={async (data) => {
              await new Promise((r) => setTimeout(r, 1000))
              const result = await invokeFunnelDev({
                locale,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                cvFile: funnelData.cvFile!,
                isFreelancer: funnelData.isFreelancer,
                jobLengths: funnelData.jobLengths,
                jobTypes: funnelData.jobTypes,
                linkedinUrl: funnelData.linkedinUrl,
                phoneNumber: data.phoneNumber,
                devPower: funnelData.devPower,
              })
              if (result) {
                emblaApi?.scrollNext()
              } else {
                toast.error($.funnelDev.errorMessage)
              }
            }}
          />
        </FunnelSlide>
        <FunnelSlide active={activeStep === 3}>
          <Success
            $={{
              title: content.successTitle,
              description: content.successMessage,
            }}
            successImage={content.successImage}
            socials={content.socials}
          />
        </FunnelSlide>
      </div>
    </div>
  )
}

interface StepProfileProps {
  $: {
    alertTitle: string
    alertDescription: string
    title: string
    linkedinSubtitle: string
    linkedinDescription: string
    linkedinLabel: string
    cvSubtitle: string
    cvDescription: string
    freelanceLabel: string
  }
  sisterFunnelIntro?: RichTextValue
  sisterFunnelCta?: SanityCtaProps
  onContinue: (data: {
    linkedinUrl: string
    cvFile: File
    isFreelancer: boolean
  }) => void
}

function StepProfile({
  $,
  sisterFunnelIntro,
  sisterFunnelCta,
  onContinue,
}: StepProfileProps) {
  const { $: translations } = useLocaleContext()
  const $locale = translations.funnelDev
  const profileStepSchema = createProfileStepSchema($locale.validation)
  const [linkedinUrl, setLinkedinUrl] = useState("")
  const [cvFile, setCvFile] = useState<File | null>(null)
  const [isFreelancer, setIsFreelancer] = useState(false)
  const [errors, setErrors] = useState<{
    linkedinUrl?: string
    cvFile?: string
    isFreelancer?: string
  }>({})

  const hasValidCv = !!cvFile && isAllowedCvExtension(cvFile.name)
  const isValid =
    profileStepSchema.safeParse({ linkedinUrl, isFreelancer }).success &&
    hasValidCv

  return (
    <div className="mx-auto max-w-6xl">
      {($.alertTitle || $.alertDescription) && (
        <div className="border-l-2 border-viola-500 bg-viola-50 px-6 py-4">
          <p className="text-md font-bold">{$.alertTitle}</p>
          {$.alertDescription && (
            <p className="mt-1 font-family-gthaptik font-thin text-grey-200">
              {$.alertDescription}
            </p>
          )}
        </div>
      )}
      <p className="mt-6 font-family-gtpressura text-xs font-light text-neutral-dark">
        Step 1 {$locale.of} 3
      </p>
      <h2 className="mt-6 font-family-gthaptik text-xl font-light text-primary-800">
        {$.title}
      </h2>
      <h3 className="mt-4 font-family-gtpressura text-xlg text-neutral-dark">
        {$.linkedinSubtitle}
      </h3>
      <p className="mt-1 font-family-gtpressura text-md font-light text-neutral-dark">
        {$.linkedinDescription}
      </p>
      <Input
        value={linkedinUrl}
        onChange={(e) => {
          setLinkedinUrl(e.target.value)
          setErrors((prev) => ({ ...prev, linkedinUrl: undefined }))
        }}
        label={$.linkedinLabel}
        className="mt-4"
      />
      {errors.linkedinUrl && (
        <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
          {errors.linkedinUrl}
        </p>
      )}
      <h3 className="mt-4 font-family-gtpressura text-xlg text-neutral-dark">
        {$.cvSubtitle}
      </h3>
      <p className="mt-1 font-family-gtpressura text-md font-light text-neutral-dark">
        {$.cvDescription}
      </p>
      <Dropzone
        file={cvFile}
        onFileChange={(file) => {
          setCvFile(file)
          setErrors((prev) => ({ ...prev, cvFile: undefined }))
        }}
        className="mt-4"
      />
      {errors.cvFile && (
        <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
          {errors.cvFile}
        </p>
      )}
      <Switch
        checked={isFreelancer}
        onCheckedChange={(checked) => {
          setIsFreelancer(checked)
          setErrors((prev) => ({ ...prev, isFreelancer: undefined }))
        }}
        label={$.freelanceLabel}
        className="mt-4"
        required
      />
      {errors.isFreelancer && (
        <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
          {errors.isFreelancer}
        </p>
      )}
      <NextPrev
        className="mt-6"
        canProceed={isValid}
        showPrev={false}
        onNext={() => {
          const selectedCvFile = cvFile
          if (!selectedCvFile) {
            setErrors((prev) => ({
              ...prev,
              cvFile: $locale.validation.cvFormatInvalid,
            }))
            return
          }
          const validation = profileStepSchema.safeParse({
            linkedinUrl,
            isFreelancer,
          })
          const nextErrors: {
            linkedinUrl?: string
            cvFile?: string
            isFreelancer?: string
          } = {}
          if (!validation.success) {
            for (const issue of validation.error.issues) {
              const path = issue.path[0]
              if (path === "linkedinUrl") {
                nextErrors.linkedinUrl = issue.message
              }
              if (path === "isFreelancer") {
                nextErrors.isFreelancer = issue.message
              }
            }
          }
          if (!isAllowedCvExtension(selectedCvFile.name)) {
            nextErrors.cvFile = $locale.validation.cvFormatInvalid
          }
          if (
            nextErrors.linkedinUrl ||
            nextErrors.cvFile ||
            nextErrors.isFreelancer
          ) {
            setErrors(nextErrors)
            return
          }
          setErrors({})
          onContinue({
            linkedinUrl: linkedinUrl.trim(),
            isFreelancer,
            cvFile: selectedCvFile,
          })
        }}
      />
      <div className="mt-24">
        {sisterFunnelIntro?.length ? (
          <RichText className="text-left" value={sisterFunnelIntro} />
        ) : null}
        {sisterFunnelCta ? (
          <div className="mt-8 flex justify-center">
            <Cta className="md:mr-auto" sanity={sisterFunnelCta} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

interface PreferencesProps {
  $: {
    title: string
    devPowerLabel: string
    devPowerPlaceholder: string
    jobTypeLabel: string
    jobLengthLabel: string
  }
  jobTypes: FunnelDevPreferenceType[]
  jobLengths: FunnelDevPreferenceType[]
  onBack: () => void
  onContinue: (data: {
    devPower: string
    jobTypes: FunnelDevPreferenceType[]
    jobLengths: FunnelDevPreferenceType[]
  }) => void
}
function Preferences({
  $,
  jobTypes,
  jobLengths,
  onBack,
  onContinue,
}: PreferencesProps) {
  const messages = useLocaleContext().$.funnelDev.validation
  const preferencesSchema = createPreferencesSchema(messages)
  const [devPower, setDevPower] = useState("")
  const [selectedJobTypes, setSelectedJobTypes] = useState<
    FunnelDevPreferenceType[]
  >([])
  const [selectedJobLengths, setSelectedJobLengths] = useState<
    FunnelDevPreferenceType[]
  >([])
  const [errors, setErrors] = useState<{
    devPower?: string
    jobTypes?: string
    jobLengths?: string
  }>({})

  const isValid = preferencesSchema.safeParse({
    devPower,
    jobTypes: selectedJobTypes,
    jobLengths: selectedJobLengths,
  }).success

  return (
    <div className="mx-auto max-w-6xl">
      <div
        className="flex cursor-pointer items-center gap-2 font-family-gtpressura text-xs font-light text-neutral-dark"
        onClick={onBack}
      >
        <ArrowLeftCircle strokeWidth={1} />
        <p>Step 2 di 3</p>
      </div>
      <h2 className="mt-6 font-family-gthaptik text-xl font-light text-primary-800">
        {$.title}
      </h2>
      <Textarea
        className="mt-6"
        label={$.devPowerLabel || "Qual è il tuo dev power?"}
        placeholder={
          $.devPowerPlaceholder ||
          "Raccontaci stack, seniority e il tipo di problemi che risolvi meglio."
        }
        value={devPower}
        required
        onChange={(e) => {
          setDevPower(e.target.value)
          setErrors((prev) => ({ ...prev, devPower: undefined }))
        }}
      />
      {errors.devPower && (
        <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
          {errors.devPower}
        </p>
      )}
      <h2 className="mt-8 font-family-gthaptik text-xl font-light text-primary-800">
        {$.jobTypeLabel}
      </h2>
      <div className="mt-6 flex items-center gap-10">
        {jobTypes.map((jobType, index) => (
          <PreferenceCheckbox
            key={index}
            selectedPreferences={selectedJobTypes}
            selectedPreference={jobType}
            setErrors={() => {
              setErrors((prev) => ({ ...prev, jobTypes: undefined }))
            }}
            setSelectedPreferences={setSelectedJobTypes}
          />
        ))}
      </div>
      {errors.jobTypes && (
        <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
          {errors.jobTypes}
        </p>
      )}
      <h2 className="mt-8 font-family-gthaptik text-xl font-light text-primary-800">
        {$.jobLengthLabel}
      </h2>
      <div className="mt-6 flex items-center gap-10">
        {jobLengths.map((jobLength, index) => (
          <PreferenceCheckbox
            key={index}
            selectedPreferences={selectedJobLengths}
            selectedPreference={jobLength}
            setErrors={() => {
              setErrors((prev) => ({ ...prev, jobLengths: undefined }))
            }}
            setSelectedPreferences={setSelectedJobLengths}
          />
        ))}
      </div>
      {errors.jobLengths && (
        <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
          {errors.jobLengths}
        </p>
      )}
      <NextPrev
        className="mt-8"
        onPrev={onBack}
        onNext={() => {
          const validation = preferencesSchema.safeParse({
            devPower,
            jobTypes: selectedJobTypes,
            jobLengths: selectedJobLengths,
          })
          if (!validation.success) {
            const nextErrors: {
              devPower?: string
              jobTypes?: string
              jobLengths?: string
            } = {}
            for (const issue of validation.error.issues) {
              const path = issue.path[0]
              if (path === "devPower") {
                nextErrors.devPower = issue.message
              }
              if (path === "jobTypes") {
                nextErrors.jobTypes = issue.message
              }
              if (path === "jobLengths") {
                nextErrors.jobLengths = issue.message
              }
            }
            setErrors(nextErrors)
            return
          }
          setErrors({})
          onContinue({
            devPower: validation.data.devPower,
            jobTypes: validation.data.jobTypes,
            jobLengths: validation.data.jobLengths,
          })
        }}
        canProceed={isValid}
      />
    </div>
  )
}

function PreferenceCheckbox({
  selectedPreferences,
  selectedPreference,
  setErrors,
  setSelectedPreferences,
}: {
  selectedPreferences: FunnelDevPreferenceType[]
  selectedPreference: FunnelDevPreferenceType
  setErrors: () => void
  setSelectedPreferences: Dispatch<SetStateAction<FunnelDevPreferenceType[]>>
}) {
  return (
    <Checkbox
      label={selectedPreference.label}
      checked={selectedPreferences.some(
        (jt) => jt.value === selectedPreference.value
      )}
      onChange={(checked) => {
        setErrors()
        if (checked) {
          setSelectedPreferences((prev) =>
            prev.some((jt) => jt.value === selectedPreference.value)
              ? prev
              : [...prev, selectedPreference]
          )
        } else {
          setSelectedPreferences((prev) =>
            prev.filter((jt) => jt.value !== selectedPreference.value)
          )
        }
      }}
    />
  )
}

interface ContactProps {
  $: {
    title: string
    firstNameLabel: string
    lastNameLabel: string
    emailLabel: string
    phoneNumberLabel: string
  }
  privacyLink: ReactNode
  onContinue: (data: {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
  }) => Promise<void>
  onBack: () => void
}
function Contact({ $, privacyLink, onBack, onContinue }: ContactProps) {
  const messages = useLocaleContext().$.funnelDev.validation
  const contactSchema = createContactSchema(messages)
  const [isLoading, setIsLoading] = useState(false)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [errors, setErrors] = useState<{
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
  }>({})

  const isValid =
    contactSchema.safeParse({
      firstName,
      lastName,
      email,
      phoneNumber,
    }).success && privacyAccepted

  return (
    <div className="mx-auto max-w-6xl">
      <div
        className="flex cursor-pointer items-center gap-2 font-family-gtpressura text-xs font-light text-neutral-dark"
        onClick={onBack}
      >
        <ArrowLeftCircle strokeWidth={1} />
        <p>Step 3 di 3</p>
      </div>
      <h2 className="mt-6 font-family-gthaptik text-xl font-light text-primary-800">
        {$.title}
      </h2>
      <div className="mt-6 flex w-full flex-col gap-6 md:flex-row">
        <div className="w-full">
          <Input
            label={$.firstNameLabel}
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value)
              setErrors((prev) => ({ ...prev, firstName: undefined }))
            }}
            className="w-full"
            required
          />
          {errors.firstName && (
            <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
              {errors.firstName}
            </p>
          )}
        </div>
        <div className="w-full">
          <Input
            label={$.lastNameLabel}
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value)
              setErrors((prev) => ({ ...prev, lastName: undefined }))
            }}
            className="w-full"
            required
          />
          {errors.lastName && (
            <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
              {errors.lastName}
            </p>
          )}
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-6 md:flex-row">
        <div className="flex-2">
          <Input
            label={$.emailLabel}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setErrors((prev) => ({ ...prev, email: undefined }))
            }}
            className="w-full"
            type="email"
            required
          />
          {errors.email && (
            <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
              {errors.email}
            </p>
          )}
        </div>
        <div className="flex-1">
          <Input
            label={$.phoneNumberLabel}
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value)
              setErrors((prev) => ({ ...prev, phoneNumber: undefined }))
            }}
            className="w-full"
            type="tel"
            required
          />
          {errors.phoneNumber && (
            <p className="text-red-700 mt-2 font-family-gtpressura text-sm">
              {errors.phoneNumber}
            </p>
          )}
        </div>
      </div>
      <Checkbox
        className="mt-4"
        label={privacyLink}
        checked={privacyAccepted}
        onChange={setPrivacyAccepted}
      />
      <NextPrev
        className="mt-6"
        onPrev={onBack}
        onNext={async () => {
          const validation = contactSchema.safeParse({
            firstName,
            lastName,
            email,
            phoneNumber,
          })
          if (!validation.success) {
            const nextErrors: {
              firstName?: string
              lastName?: string
              email?: string
              phoneNumber?: string
            } = {}
            for (const issue of validation.error.issues) {
              const path = issue.path[0]
              if (path === "firstName") {
                nextErrors.firstName = issue.message
              }
              if (path === "lastName") {
                nextErrors.lastName = issue.message
              }
              if (path === "email") {
                nextErrors.email = issue.message
              }
              if (path === "phoneNumber") {
                nextErrors.phoneNumber = issue.message
              }
            }
            setErrors(nextErrors)
            return
          }
          setErrors({})
          setIsLoading(true)
          await onContinue({
            firstName: validation.data.firstName,
            lastName: validation.data.lastName,
            email: validation.data.email,
            phoneNumber: validation.data.phoneNumber,
          })
          setIsLoading(false)
        }}
        canProceed={isValid && !isLoading}
        isNextLoading={isLoading}
      />
    </div>
  )
}

interface SuccessProps {
  $: {
    title: string
    description: string
  }
  successImage?: {
    asset: SanityReference<SanityImageAsset>
    alt?: string
  }
  socials: {
    title?: string
    url: string
    image?: {
      asset: SanityReference<SanityImageAsset>
    }
  }[]
}

function Success({
  $: { title, description },
  successImage,
  socials,
}: SuccessProps) {
  const $ = useLocaleContext().$.funnelDev

  return (
    <div className="mx-auto max-w-3xl">
      <h2 className="text-2xl font-bold text-neutral-dark">{title}</h2>
      {successImage?.asset && (
        <SanityImageField
          asset={successImage}
          fill="width"
          className="mt-6 rounded-sm object-cover"
        />
      )}
      <p className="mt-8 font-family-gtpressura text-xlg text-neutral-dark opacity-55">
        {description}
      </p>
      {!!socials.length && (
        <div className="mt-6 flex items-center justify-center gap-4">
          {socials.map((social, i) =>
            social.image?.asset ? (
              <SocialIconLink
                key={i}
                url={social.url}
                asset={social.image.asset}
                color="blue"
                title={social.title || "social icon"}
              />
            ) : null
          )}
        </div>
      )}
      {/* {instagramReelEmbedUrl && (
        <iframe
          className="mt-4 h-[763px] w-full rounded-sm border border-viola-200"
          src={instagramReelEmbedUrl}
          title="Devpunks Instagram reel"
          loading="lazy"
          allowFullScreen
          scrolling="no"
          referrerPolicy="no-referrer-when-downgrade"
        />
      )} */}
      {/* <div className="mt-6 flex items-center justify-center gap-4">
        <a
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full border border-viola-200 p-3 text-primary-500 transition-colors hover:bg-viola-50"
          aria-label="Open Devpunks Instagram"
        >
          <Instagram />
        </a>
        <a
          href={linkedInUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full border border-viola-200 p-3 text-primary-500 transition-colors hover:bg-viola-50"
          aria-label="Open Devpunks LinkedIn"
        >
          <Linkedin />
        </a>
      </div> */}
      <div className="mt-4 flex justify-center">
        <Cta
          sanity={{
            internalLink: {
              slug: "/",
            },
            type: "internal",
          }}
        >
          {$.goHome}
        </Cta>
      </div>
    </div>
  )
}

function FunnelSlide({
  children,
  active,
  className,
}: {
  children: React.ReactNode
  active: boolean
  className?: string
}) {
  const slideRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const slide = slideRef.current
    if (!slide) return
    if (active) {
      slide.removeAttribute("inert")
      return
    }

    const focusedElement = document.activeElement
    if (
      focusedElement instanceof HTMLElement &&
      slide.contains(focusedElement)
    ) {
      focusedElement.blur()
    }
    slide.setAttribute("inert", "")
  }, [active])

  return (
    <div
      ref={slideRef}
      aria-hidden={!active}
      className="flex-[0_0_100%] px-4"
      data-active={active}
    >
      <div
        className={cn(
          "mx-auto max-w-[138rem] [&_h1]:text-left [&_h2]:text-left [&_h3]:text-left [&_h4]:text-left [&_label]:text-left [&_p]:text-left",
          className
        )}
      >
        {children}
      </div>
    </div>
  )
}
function NextPrev({
  className,
  onNext,
  onPrev,
  showPrev = true,
  isNextLoading,
  canProceed,
}: {
  className?: string
  onNext: () => Promise<void> | void
  onPrev?: () => void
  showPrev?: boolean
  isNextLoading?: boolean
  canProceed?: boolean
}) {
  const $ = useLocaleContext().$.funnelDev
  return (
    <div
      className={cn(
        "flex gap-4 font-family-gtpressura text-lg text-primary-500 uppercase",
        !showPrev && "justify-end",
        className
      )}
    >
      {showPrev && (
        <button className="flex items-center gap-2 px-6 py-3" onClick={onPrev}>
          <ArrowLeft />
          {$.goBack}
        </button>
      )}
      <button
        className={cn(
          "flex items-center gap-2 px-6 py-3",
          !canProceed && "cursor-not-allowed opacity-50"
        )}
        onClick={() => canProceed && onNext()}
      >
        {isNextLoading && <LoaderCircle className="animate-spin" />}
        {$.proceed}
        <ArrowRight />
      </button>
    </div>
  )
}
